import InfiniteScroll from 'react-infinite-scroll-component';
import { Role, rolesManipulator } from 'auth';
import classNames from 'classnames';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import IncidentTypePriorityLabel from '@/modules/common/components/IncidentTypePriority.tsx';
import IncidentItem from '@/modules/common/components/items/IncidentItem.tsx';
import IncidentInfoModal, {
  IncidentInfoModalProps,
} from '@/modules/common/components/modals/IncidentInfoModal.tsx';
import { useGetCurrentBusinessId } from '@/modules/common/hooks/useGetCurrentBusinessId.ts';
import { useIncidentsListQuery } from '@/modules/common/queries/incidents.ts';
import ArchivedItemAction from '@/modules/admin-dashboard/components/ArchivedItemAction.tsx';
import IncidentTypeModal from '@/modules/admin-dashboard/components/modals/IncidentTypeModal.tsx';
import {
  useDeleteIncidentTypeMutation,
  useRestoreIncidentTypeMutation,
} from '@/modules/admin-dashboard/queries/incidents.ts';
import { adminDashboardPaths } from '@/modules/admin-dashboard/routes.tsx';
import { ConfirmModal, DisclosureListItem, DropdownMenu, ModalProviderContext, Spinner } from 'ui';
import { ChevronDownIcon, DotsHorizontalIcon } from 'ui/icons';
import { IncidentTypeWithRelationsResponse } from 'compliance-api';

interface IncidentItemProps {
  archived?: boolean;
  incidentType: IncidentTypeWithRelationsResponse;
  filterByName?: string;
  clientId?: number;
}

const IncidentTypeItem = ({
  archived,
  incidentType,
  filterByName,
  clientId,
}: IncidentItemProps) => {
  const [open, setOpen] = useState(false);
  const disclosureRef = useRef<HTMLDivElement>(null);
  const prevFilterByNameRef = useRef<string | undefined>();
  const businessId = useGetCurrentBusinessId();

  useEffect(() => {
    const wasSearching = !!prevFilterByNameRef.current;
    const isSearching = !!filterByName;

    if (isSearching && !wasSearching && !open) {
      const timer = setTimeout(() => {
        disclosureRef.current?.click();
      }, 500); // small timeout added to not disclose items that will be removed in the next render
      return () => clearTimeout(timer);
    }
  }, [filterByName, open]);

  useEffect(() => {
    prevFilterByNameRef.current = filterByName;
  });

  const [incidentInfo, setIncidentInfo] = useState<null | Pick<
    IncidentInfoModalProps,
    'incident' | 'defaultActiveTab'
  >>(null);
  const { openModal } = useContext(ModalProviderContext);

  const {
    id,
    name,
    form,
    form_id,
    priority,
    department,
    deleted_at,
    responsible_department_id,
    total_result_quantity,
    created_with_ai,
    completion_form,
  } = incidentType;

  const { mutateAsync: deleteIncident } = useDeleteIncidentTypeMutation();

  const {
    data: incidentsData,
    isLoading: isIncidentsLoading,
    fetchNextPage: fetchNextIncidentsPage,
    hasNextPage: hasNextIncidentsPage,
  } = useIncidentsListQuery(
    {
      page: 1,
      page_size: 15,
      filter_by_incident_type: id,
      filter_by_deleted: archived,
      filter_by_name: filterByName,
      filter_by_client_id: clientId,
    },
    !open,
  );

  const incidents = (incidentsData?.pages || []).flatMap(({ data }) => data);

  const userRole = rolesManipulator.current_user_role;
  const isUserAdmin = userRole === Role.COMPLIANCE_ADMIN;

  const handleEdit = useCallback(() => {
    openModal(IncidentTypeModal, {
      defaultValues: { name, form_id, priority, responsible_department_id, id },
      defaultPaginateOptions: {
        department: { label: department?.name, value: department?.id || 0 },
        form: { label: form.name, value: form.id },
        resolve_form: completion_form?.id
          ? {
              label: completion_form?.name,
              value: completion_form?.id,
            }
          : undefined,
      },
    });
  }, [department, form, form_id, id, name, openModal, priority, responsible_department_id]);

  const handleDelete = useCallback(() => {
    openModal(ConfirmModal, {
      title: 'Archive event type?',
      onConfirm: async () => {
        deleteIncident(id);
      },
    });
  }, [deleteIncident, id, openModal]);

  const rightContent = (
    <div
      className={classNames(
        'w-full flex items-center justify-between gap-4 max-w-[740px] xl:max-w-[800px]',
      )}
    >
      <div className="flex items-center gap-4">
        <div className="hidden md:flex w-[85px] flex-col">
          <span className="text-neutral-500 text-xs">Priority</span>
          <span className="text-sm">
            <IncidentTypePriorityLabel priority={priority} />
          </span>
        </div>
        <div className="hidden md:flex w-[120px] xl:w-[180px] flex-col">
          <span className="text-neutral-500 text-xs">Report Form</span>
          <Link
            to={{
              pathname: adminDashboardPaths.dynamic.form(form.id),
              search: `?business_id=${businessId}`,
            }}
            replace={false}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-ellipsis overflow-hidden whitespace-nowrap hover:underline"
          >
            {form.reference_name} {form.name}
          </Link>
        </div>
        {completion_form?.id && (
          <div className="hidden md:flex w-[120px] xl:w-[180px] flex-col">
            <span className="text-neutral-500 text-xs">Resolve Form</span>
            <Link
              to={{
                pathname: adminDashboardPaths.dynamic.form(completion_form.id),
                search: `?business_id=${businessId}`,
              }}
              replace={false}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-ellipsis overflow-hidden whitespace-nowrap hover:underline"
            >
              {completion_form.reference_name} {completion_form.name}
            </Link>
          </div>
        )}
        <div className="hidden lg:flex flex-col w-[220px] max-w-[220px]">
          <span className="text-neutral-500 text-xs">Department</span>
          <span className="max-w-[220px] w-max truncate text-sm">{department?.name}</span>
        </div>
      </div>
      <div className="flex gap-4 items-center">
        <div className="flex">
          {!!total_result_quantity && (
            <img
              src={ChevronDownIcon}
              alt="open"
              className={classNames('transition-all w-6 h-6 min-w-6 min-h-6', {
                'rotate-180': open,
              })}
            />
          )}
        </div>
        {isUserAdmin && (
          <DropdownMenu
            items={[
              { label: 'Edit', onClick: handleEdit },
              { label: 'Archive', onClick: handleDelete },
            ]}
            button={
              <button className="flex items-center justify-center">
                <img alt="actions" src={DotsHorizontalIcon} className="w-6 h-6 min-w-6 min-h-6" />
              </button>
            }
            className="w-[180px] divide-white"
          />
        )}
      </div>
    </div>
  );

  const bottomCustomContent = useMemo(
    () =>
      !!total_result_quantity &&
      !deleted_at && (
        <div className="border-t">
          {isIncidentsLoading && <Spinner size="md" className="m-auto my-2" />}
          {!isIncidentsLoading && (
            <div id={`incident_type_list${id}`} className="max-h-56 overflow-y-auto">
              <InfiniteScroll
                next={fetchNextIncidentsPage}
                hasMore={!!hasNextIncidentsPage}
                loader={
                  <div className="justify-items-center my-2">
                    <Spinner size="md" />
                  </div>
                }
                dataLength={incidents.length}
                style={{ overflow: 'unset' }}
                scrollableTarget={`incident_type_list${id}`}
              >
                {incidents.map(incident => (
                  <IncidentItem
                    key={incident.id}
                    incident={incident}
                    isUserAdmin={isUserAdmin}
                    responsibleDepartmentId={responsible_department_id}
                    setIncidentInfo={setIncidentInfo}
                    incidentType={incidentType}
                    clientId={clientId}
                  />
                ))}
              </InfiniteScroll>
            </div>
          )}
        </div>
      ),
    [
      id,
      incidents,
      deleted_at,
      isUserAdmin,
      isIncidentsLoading,
      hasNextIncidentsPage,
      fetchNextIncidentsPage,
      total_result_quantity,
      responsible_department_id,
    ],
  );

  return (
    <>
      <DisclosureListItem
        ref={disclosureRef}
        leftMainString={name}
        onOpen={setOpen.bind(null, true)}
        onClose={setOpen.bind(null, false)}
        classes={{
          openerArrow: 'hidden',
          rightContent: 'hidden sm:hidden',
          leftContent: 'w-[270px] md:w-auto',
          rightContentUp: '!flex justify-end',
          container: classNames('!p-0', { 'bg-gray-100': deleted_at }),
          content: classNames('!p-4 grid grid-cols-[25%_75%]'),
        }}
        leftSubduedContentSet={
          created_with_ai
            ? [
                total_result_quantity
                  ? `${total_result_quantity} registered events`
                  : 'No registered events',
                <CreatedByAiLabel created_with_ai={created_with_ai} />,
              ]
            : [
                total_result_quantity
                  ? `${total_result_quantity} registered events`
                  : 'No registered events',
              ]
        }
        bottomCustomContent={bottomCustomContent}
        rightContent={
          deleted_at ? (
            <ArchivedItemAction id={id} mutation={useRestoreIncidentTypeMutation} />
          ) : (
            rightContent
          )
        }
      />
      {incidentInfo && (
        <IncidentInfoModal
          incidentType={incidentType}
          incident={incidentInfo.incident}
          defaultActiveTab={incidentInfo.defaultActiveTab}
          onClose={setIncidentInfo.bind(null, null)}
          fromClientPage={!!clientId}
        />
      )}
    </>
  );
};

export default IncidentTypeItem;
